"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/lib/api";
import { Skeleton } from "@/components/Skeleton";

interface State {
  loading: boolean;
  showInlineConversion: boolean;
  // Region-aware preview. The "(~ R$1.2M)" projection is BR-specific
  // and must not appear in the default English UX per the locked
  // jurisdiction-scoping rules (CLAUDE.md 2026-05-03). We read the
  // user's display_currency from /me and pick the example accordingly:
  // BRL → BRL projection example, MXN → MXN, anything else → USD-only.
  displayCurrency: string | null;
  // EPIC-029 BR-primary display flip Story 1 + 6 — which currency
  // leads on every screen for this user. Default 'BRL' for region=BR
  // (backfilled by migration 0143); 'USDC' for non-BR. Power-user
  // toggle below flips between the two.
  displayCurrencyPrimary: 'USDC' | 'BRL';
  saving: boolean;
  error: string | null;
}

function localCurrencyExample(currency: string | null): string {
  switch (currency) {
    case 'BRL':
      return '$200K USD (~ R$1.2M)';
    case 'MXN':
      return '$200K USD (~ MX$3.4M)';
    case 'EUR':
      return '$200K USD (~ €185K)';
    case 'GBP':
      return '$200K USD (~ £160K)';
    case 'CAD':
      return '$200K USD (~ C$275K)';
    case 'INR':
      return '$200K USD (~ ₹16.7M)';
    case 'ARS':
      return '$200K USD (~ AR$200M)';
    case 'COP':
      return '$200K USD (~ COP$800M)';
    default:
      // USD/USDC or any unrecognized — no projection layer.
      return '$200K USD';
  }
}

/**
 * STORY-1805 phase 2 — toggle the inline USD→local-currency
 * annotation on market detail pages. Default ON. Power users who
 * prefer the source-only reading can flip it off here.
 *
 * Source of truth is `users.show_inline_conversion`; we mirror the
 * value to a `granbay.show_inline_conversion` cookie at toggle time
 * so the server-rendered markets/[slug] route can read it without a
 * /v1/me round-trip.
 */
export function DisplaySettings() {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({
    loading: true,
    showInlineConversion: true,
    displayCurrency: null,
    displayCurrencyPrimary: 'USDC',
    saving: false,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const me = await api.me();
        if (cancelled) return;
        const value = me.show_inline_conversion ?? true;
        // Mirror to cookie on first read so server renders match
        // even if the user never opens this page.
        document.cookie = `granbay.show_inline_conversion=${value}; path=/; max-age=31536000; SameSite=Lax`;
        setState({
          loading: false,
          showInlineConversion: value,
          displayCurrency: me.display_currency ?? null,
          displayCurrencyPrimary:
            (me.display_currency_primary as 'USDC' | 'BRL') ?? 'USDC',
          saving: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          loading: false,
          error:
            err instanceof ApiError
              ? err.message
              : t("displaySettings.errLoad"),
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  async function toggle(next: boolean) {
    setState((s) => ({ ...s, saving: true, error: null }));
    try {
      await api.updateShowInlineConversion(next);
      // Mirror to cookie so the next markets/[slug] render reflects
      // the change without waiting for hydration.
      document.cookie = `granbay.show_inline_conversion=${next}; path=/; max-age=31536000; SameSite=Lax`;
      setState((s) => ({
        ...s,
        showInlineConversion: next,
        saving: false,
      }));
    } catch (err) {
      setState((s) => ({
        ...s,
        saving: false,
        error:
          err instanceof ApiError
            ? err.message
            : t("displaySettings.errSave"),
      }));
    }
  }

  // EPIC-029 BR Story 6 — flip primary currency on every screen.
  // The /me cookie is mirrored alongside the existing show-inline
  // pattern so SSR-rendered surfaces (markets/[slug], /home, /portfolio)
  // honour the new pref on the next paint without waiting for the
  // /me round-trip.
  async function toggleDisplayCurrencyPrimary(next: 'USDC' | 'BRL') {
    setState((s) => ({ ...s, saving: true, error: null }));
    try {
      await api.updateDisplayCurrencyPrimary(next);
      document.cookie = `granbay.display_currency_primary=${next}; path=/; max-age=31536000; SameSite=Lax`;
      setState((s) => ({
        ...s,
        displayCurrencyPrimary: next,
        saving: false,
      }));
    } catch (err) {
      setState((s) => ({
        ...s,
        saving: false,
        error:
          err instanceof ApiError
            ? err.message
            : t("displaySettings.errSave"),
      }));
    }
  }

  if (state.loading) {
    return <Skeleton className="h-16 w-full" />;
  }

  // Render the example fiat string in the user's display currency only.
  // For users without a BR/MX/EU locale, the inline conversion toggle
  // is informational — they see the USD source either way — so we
  // simply show the same source value with no foreign-currency
  // projection. This avoids leaking a Brazilian "R$1.2M" reference
  // into a default English UX (see CLAUDE.md jurisdiction-scoping
  // rule, locked 2026-05-03).

  return (
    <div className="flex flex-col gap-5">
      {/* EPIC-029 BR Story 6 — primary-currency selector. Default per
          region (BRL for region=brazil, USDC otherwise) was set by
          migration 0143; this toggle is the user-facing override. */}
      <fieldset
        className="flex flex-col gap-2"
        disabled={state.saving}
        data-testid="display-currency-primary"
      >
        <legend className="text-sm font-semibold text-[var(--color-text)]">
          {t("displaySettings.primaryCurrencyLabel")}
        </legend>
        <p className="text-xs text-[var(--color-text3)]">
          {t("displaySettings.primaryCurrencyHelp")}
        </p>
        <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface2)] px-3 py-2 text-xs">
            <input
              type="radio"
              name="display-currency-primary"
              value="BRL"
              checked={state.displayCurrencyPrimary === 'BRL'}
              onChange={() => toggleDisplayCurrencyPrimary('BRL')}
              className="h-4 w-4 cursor-pointer accent-[var(--color-brand)]"
            />
            <span className="font-semibold text-[var(--color-text)]">
              R$ {t("displaySettings.primaryCurrencyBrlLabel")}
            </span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface2)] px-3 py-2 text-xs">
            <input
              type="radio"
              name="display-currency-primary"
              value="USDC"
              checked={state.displayCurrencyPrimary === 'USDC'}
              onChange={() => toggleDisplayCurrencyPrimary('USDC')}
              className="h-4 w-4 cursor-pointer accent-[var(--color-brand)]"
            />
            <span className="font-semibold text-[var(--color-text)]">
              $ {t("displaySettings.primaryCurrencyUsdcLabel")}
            </span>
          </label>
        </div>
      </fieldset>

      <hr className="border-[var(--color-border)]" />

      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={state.showInlineConversion}
          disabled={state.saving}
          onChange={(e) => toggle(e.target.checked)}
          className="mt-1 h-4 w-4 cursor-pointer accent-[var(--color-brand)]"
        />
        <span className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-[var(--color-text)]">
            {t("displaySettings.inlineConversionLabel")}
          </span>
          <span className="text-xs text-[var(--color-text3)]">
            {t("displaySettings.inlineConversionHelp")}
          </span>
          <span className="mt-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface2)] px-2 py-1 font-mono text-[11px] text-[var(--color-text2)]">
            {state.showInlineConversion
              ? localCurrencyExample(state.displayCurrency)
              : "$200K USD"}
          </span>
        </span>
      </label>
      {state.error && (
        <div
          role="alert"
          className="rounded-md border px-3 py-2 text-xs"
          style={{
            borderColor:
              "color-mix(in srgb, var(--color-status-negative) 50%, transparent)",
            background:
              "color-mix(in srgb, var(--color-status-negative) 8%, transparent)",
            color: "var(--color-status-negative)",
          }}
        >
          {state.error}
        </div>
      )}
    </div>
  );
}
